import { Link, createFileRoute } from "@tanstack/react-router";
import { m } from "framer-motion";
import {
  ArrowLeft,
  Car,
  ChevronDown,
  type LucideIcon,
  User,
} from "lucide-react";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { useAffiliateSelection } from "@/auth/affiliateSelection";
import {
  Avatar,
  Button,
  Badge,
  Card,
  CopyPlate,
  Separator,
  Skeleton,
  VehicleImage,
} from "@rumbledirect/ui-v2";
import type { DossierDetail } from "@/dossiers/dossierDetail";
import { getDossierSourceLabel } from "@/dossiers/dossierDetailLabels";
import { DEFAULT_DOSSIERS_SEARCH } from "@/dossiers/dossiersSearch";
import { UrgentMarker } from "@/dossiers/UrgentMarker";
import {
  getMediaIntentLabel,
  headerDamagePhoto,
  useDossierMedia,
} from "@/dossiers/dossierMedia";
import {
  getDossierDriverTitle,
  getServicePartnerName,
  getLicensePlateIdentifier,
  getOpdrachtgeverName,
} from "@/dossiers/dossierFields";
import { DOSSIER_DETAIL_COMPONENTS } from "@/dossiers/dossierDetailComponents";
import type {
  DossierDetailLayout,
  DossierDetailPlacement,
} from "@/dossiers/dossierDetailLayout";
import { useDossierDetailLayout } from "@/dossiers/useDossierDetailLayout";
import { useDossierWorkflowStates } from "@/dossiers/useDossierWorkflowStates";
import { useDossierWorkflowTransitions } from "@/dossiers/useDossierWorkflowTransitions";
import { useGetAdminDossiersDossierId } from "@/lib/api/generated/dossier/dossier";
import { cn } from "@rumbledirect/ui-v2/cn";
import { usePageTitle } from "@/shell/PageTitle";
import { TeamAvatar } from "@/teams/TeamAvatar";
import { DetailLayoutBody } from "@/viewConfiguration/DetailLayoutBody";
import {
  useMotionInitial,
  useMotionTransition,
} from "@rumbledirect/ui-v2/motion";
import {
  EMPTY_VALUE,
  formatDateTime,
  formatNumber,
} from "@rumbledirect/ui-v2/format";

interface DossierDetailSearch {
  /**
   * The open tab, as a bare key: which tabs exist is decided by the served
   * layout, so this cannot be a closed union. A key the layout does not carry
   * falls back to its default tab.
   */
  readonly tab: string | undefined;
  /** Id of the task shown in the task sheet; undefined means no sheet. */
  readonly task: string | undefined;
  /** The affiliate owning the dossier, set by email links. */
  readonly affiliateId?: string;
}

/**
 * The dossier detail screen. Opening a dossier marks it as read
 * (`markAsRead`), closing the loop with the list's "Ongelezen" filter and
 * KPI. The header (identity + Bestuurder/Voertuig summary) always shows;
 * below it, a two-column body holds the tabs and a sticky rail.
 *
 * Which tabs and cards that body holds is not decided here: it comes from the
 * affiliate's dossier detail view configuration (see
 * `useDossierDetailLayout`), falling back to `DEFAULT_DOSSIER_DETAIL_LAYOUT`
 * when the affiliate has none. This screen only places what the layout asks
 * for, in the order it asks for it.
 */
export const Route = createFileRoute("/_authenticated/dossiers_/$dossierId")({
  // The active tab and the open task's sheet live in the URL so a deep
  // link/reload lands back on the same view, mirroring the dossiers list
  // route's search-param pattern.
  validateSearch: (search: Record<string, unknown>): DossierDetailSearch => ({
    tab: typeof search.tab === "string" ? search.tab : undefined,
    task: typeof search.task === "string" ? search.task : undefined,
    affiliateId:
      typeof search.affiliateId === "string" ? search.affiliateId : undefined,
  }),
  component: DossierDetailRoute,
});

// Email links name the owning affiliate; switch to it before the dossier is
// fetched, so a multi-affiliate user does not load it under the wrong tenant.
// Once switched, the parameter is dropped from the URL: the switcher changes
// the affiliate without navigating, so a lingering parameter would switch
// straight back after every manual change.
function DossierDetailRoute(): ReactNode {
  const { affiliateId: linkAffiliateId } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { affiliateId, availableAffiliateIds, selectAffiliate } =
    useAffiliateSelection();
  const allowed =
    linkAffiliateId === undefined ||
    availableAffiliateIds.includes(linkAffiliateId);
  const mustSwitch =
    allowed && linkAffiliateId !== undefined && linkAffiliateId !== affiliateId;

  useEffect(() => {
    if (!allowed || linkAffiliateId === undefined) {
      return;
    }
    if (mustSwitch) {
      selectAffiliate(linkAffiliateId);
      return;
    }
    void navigate({
      search: ({ affiliateId: _consumed, ...rest }) => rest,
      replace: true,
    });
  }, [allowed, mustSwitch, linkAffiliateId, selectAffiliate, navigate]);

  if (!allowed) {
    return <p role="alert">Je hebt geen toegang tot dit dossier.</p>;
  }
  if (mustSwitch) {
    return null;
  }
  return <DossierDetailPage />;
}

function DossierDetailPage(): ReactNode {
  const { dossierId } = Route.useParams();
  const { tab, task } = Route.useSearch();
  const navigate = Route.useNavigate();

  const { data, isPending, isError, refetch } = useGetAdminDossiersDossierId(
    dossierId,
    { markAsRead: true },
  );
  usePageTitle(data && getLicensePlateIdentifier(data));
  const {
    data: layout,
    isError: isLayoutError,
    refetch: refetchLayout,
  } = useDossierDetailLayout(dossierId);

  // Entering without a tab (or with one this layout does not carry) resolves to
  // the layout's default tab; writing that back as a replace keeps the URL
  // shareable without adding a history entry the back button has to walk past.
  const onTabResolved = useCallback(
    (nextTab: string | undefined) => {
      void navigate({
        search: (prev) => ({ ...prev, tab: nextTab }),
        replace: true,
      });
    },
    [navigate],
  );

  return (
    <div className="space-y-4">
      <Link
        to="/dossiers"
        search={DEFAULT_DOSSIERS_SEARCH}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Terug naar dossiers
      </Link>

      {/* Visually hidden; the header card below carries the visible title. */}
      <h1 className="sr-only">Dossier</h1>

      {isError || isLayoutError ? (
        // A layout that cannot be fetched (anything but the 404 that means "no
        // configuration") is as fatal as a dossier that cannot be fetched:
        // rendering the fallback layout instead would show cards the server
        // never approved.
        <DossierDetailError
          onRetry={() => {
            void refetch();
            void refetchLayout();
          }}
        />
      ) : (
        <>
          <DossierDetailHeader dossier={data} isPending={isPending} />
          {data && layout && (
            <DossierDetailBody
              dossier={data}
              layout={layout}
              tab={tab}
              task={task}
              onTaskChange={(taskId) => {
                void navigate({
                  search: (prev) => ({ ...prev, task: taskId }),
                });
              }}
              onTabResolved={onTabResolved}
              onTabChange={(nextTab) => {
                void navigate({
                  search: (prev) => ({ ...prev, tab: nextTab }),
                });
              }}
            />
          )}
        </>
      )}
    </div>
  );
}

interface DossierDetailBodyProps {
  readonly dossier: DossierDetail;
  readonly layout: DossierDetailLayout;
  readonly tab: string | undefined;
  readonly task: string | undefined;
  readonly onTaskChange: (taskId: string | undefined) => void;
  readonly onTabChange: (tab: string) => void;
  /** Called with the resolved tab when the URL does not already name it. */
  readonly onTabResolved: (tab: string | undefined) => void;
}

// The two-column body: a Tabs main column plus a right rail that stays in view
// while the main column scrolls, mirroring the Ledger concept's detail layout.
// Both columns are filled from the served layout.
function DossierDetailBody({
  dossier,
  layout,
  tab,
  task,
  onTaskChange,
  onTabChange,
  onTabResolved,
}: DossierDetailBodyProps): ReactNode {
  // Whether the Status card has anything to show has to be known before the
  // rail is laid out, so the body owns the workflow-states query and hands
  // the card its steps. A failed request drops the placement; the Acties card
  // reads the same endpoint and renders the recoverable error.
  const workflowStatesQuery = useDossierWorkflowStates(dossier.id);
  // Shares its cache entry with the Acties card's own transitions query (same
  // dossier id, no params) — see `DossierDetailComponentContext.hasAvailableWorkflowTransitions`.
  // `undefined` while pending or failed (e.g. a 403 for a user who can read
  // the dossier but not update it): only a confirmed, successfully-fetched
  // empty list means "no transitions", never a query that hasn't answered.
  const transitionsQuery = useDossierWorkflowTransitions(dossier.id);

  return (
    <DetailLayoutBody
      layout={layout}
      registry={DOSSIER_DETAIL_COMPONENTS}
      tab={tab}
      onTabChange={onTabChange}
      onTabResolved={onTabResolved}
      context={{
        dossier,
        workflowSteps: workflowStatesQuery.states,
        hasAvailableWorkflowTransitions: transitionsQuery.isSuccess
          ? transitionsQuery.transitions.length > 0
          : undefined,
        openTaskId: task,
        onTaskChange,
      }}
      renderTab={(components, renderPlacement) => (
        <DossierDetailTabBody
          components={components}
          renderPlacement={renderPlacement}
        />
      )}
    />
  );
}

/**
 * One tab's body: its `main` components stacked, plus a side column when the
 * tab places anything in its own `aside` slot (as Schadebeeld does with the
 * photo gallery). `items-start` so the two columns size independently.
 */
function DossierDetailTabBody({
  components,
  renderPlacement,
}: {
  readonly components: readonly DossierDetailPlacement[];
  readonly renderPlacement: (placement: DossierDetailPlacement) => ReactNode;
}): ReactNode {
  const main = components.filter((placement) => placement.slot !== "aside");
  const aside = components.filter((placement) => placement.slot === "aside");

  if (aside.length === 0) {
    return (
      <div className="flex flex-col gap-5">{main.map(renderPlacement)}</div>
    );
  }

  return (
    <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(22rem,0.8fr)]">
      <div className="flex min-w-0 flex-col gap-5">
        {main.map(renderPlacement)}
      </div>
      <div className="flex flex-col gap-5">{aside.map(renderPlacement)}</div>
    </div>
  );
}

interface DossierDetailErrorProps {
  readonly onRetry: () => void;
}

function DossierDetailError({ onRetry }: DossierDetailErrorProps): ReactNode {
  return (
    <Card role="alert" className="p-10 text-center">
      <p className="mb-3 text-muted-foreground">
        We konden dit dossier niet laden.
      </p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        Opnieuw proberen
      </Button>
    </Card>
  );
}

interface DossierDetailHeaderProps {
  readonly dossier: DossierDetail | undefined;
  readonly isPending: boolean;
}

// The dossier detail header: an identity row (copy-to-clipboard plate,
// driver, opdrachtgever, service partner, status, dossier number/created date) plus
// a collapsible core-fields grid (Bestuurder, Voertuig) — mirroring the
// Ledger concept's detail header (its tabs and action rail are deliberately
// deferred to a later rung).
function DossierDetailHeader({
  dossier,
  isPending,
}: DossierDetailHeaderProps): ReactNode {
  const headerInitial = useMotionInitial({ opacity: 0, y: 12 });
  const summaryTransition = useMotionTransition({
    duration: 0.25,
    ease: [0.22, 1, 0.36, 1],
  });
  // Open by default: the core Bestuurder/Voertuig facts are what most admins
  // come to a dossier for, so hiding them behind a click by default would
  // cost more than it saves. The toggle exists for once the header grows
  // busier (tabs, action rail, ...) and this grid is no longer always the
  // first thing needed.
  const [summaryOpen, setSummaryOpen] = useState(true);
  // The header shows a real photo of the vehicle when the dossier has one.
  // Shares its query with the Schadebeeld tab, which React Query de-duplicates,
  // so this costs no extra request.
  const { data: media } = useDossierMedia(dossier?.id);
  const headerPhoto = headerDamagePhoto(media?.member ?? []);

  if (isPending || dossier === undefined) {
    return <DossierDetailHeaderSkeleton />;
  }

  const licensePlateIdentifier = getLicensePlateIdentifier(dossier);
  const driverTitle = getDossierDriverTitle(dossier);
  const fleetName = dossier.sharingServiceCompany?.fullName;
  const opdrachtgever = getOpdrachtgeverName(dossier);
  const servicePartner = getServicePartnerName(dossier);
  const createdAt = dossier.createdAt
    ? formatDateTime(dossier.createdAt)
    : null;
  const sourceLabel = dossier.source
    ? getDossierSourceLabel(dossier.source)
    : null;
  const teamName = dossier.userCollection?.name;
  const assigneeName = dossier.assignee?.fullName;
  const vehicleLabel = [dossier.vehicle.brand, dossier.vehicle.model]
    .filter((part): part is string => Boolean(part))
    .join(" ");

  return (
    <m.div
      initial={headerInitial}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
    >
      <Card className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5">
            {licensePlateIdentifier !== undefined && (
              <CopyPlate value={licensePlateIdentifier} size="md" />
            )}
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
              <span
                className={cn(
                  "min-w-0 truncate text-xl tracking-tight",
                  !fleetName && driverTitle.isFallback
                    ? "font-normal text-muted-foreground"
                    : "font-semibold",
                )}
              >
                {fleetName ? vehicleLabel || "Voertuig" : driverTitle.label}
              </span>
              {fleetName ? (
                <Badge>{fleetName}</Badge>
              ) : (
                opdrachtgever && <HeaderMeta>{opdrachtgever}</HeaderMeta>
              )}
              {servicePartner && <HeaderMeta>{servicePartner}</HeaderMeta>}
              {dossier.urgent === true && (
                <UrgentMarker className="translate-y-1" />
              )}
            </div>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-x-5 gap-y-2">
            <button
              type="button"
              onClick={() => {
                setSummaryOpen((prev) => !prev);
              }}
              aria-expanded={summaryOpen}
              aria-controls="dossier-summary"
              className="flex shrink-0 items-center gap-1.5 rounded-xl border border-border/70 bg-muted/40 px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground"
            >
              {summaryOpen ? "Verberg gegevens" : "Toon gegevens"}
              <ChevronDown
                aria-hidden="true"
                className={cn(
                  "size-4 transition-transform",
                  summaryOpen && "rotate-180",
                )}
              />
            </button>
          </div>
        </div>

        <m.div
          id="dossier-summary"
          initial={false}
          animate={{
            height: summaryOpen ? "auto" : 0,
            opacity: summaryOpen ? 1 : 0,
          }}
          transition={summaryTransition}
          className="overflow-hidden"
        >
          <Separator className="my-4" />

          <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:gap-8">
            <div className="grid flex-1 gap-x-8 gap-y-5 sm:grid-cols-2">
              {!fleetName && (
                <FieldGroup
                  title="Bestuurder"
                  icon={User}
                  fields={[
                    {
                      label: "Naam",
                      value: dossier.driver?.fullName ?? undefined,
                    },
                    {
                      label: "E-mail",
                      value: dossier.driver?.email ?? undefined,
                      href: dossier.driver?.email
                        ? `mailto:${dossier.driver.email}`
                        : undefined,
                    },
                    {
                      label: "Telefoon",
                      // Displays the Dutch national format (e.g. "06 12345678"); the
                      // link itself dials the E164 number so it works regardless of
                      // where the number is read from.
                      value: dossier.driver?.phone
                        ? (dossier.driver.phoneFormatted ??
                          dossier.driver.phone)
                        : undefined,
                      href: dossier.driver?.phone
                        ? `tel:${dossier.driver.phone}`
                        : undefined,
                    },
                  ]}
                />
              )}
              <FieldGroup
                title="Voertuig"
                icon={Car}
                fields={[
                  { label: "Merk & model", value: vehicleLabel || undefined },
                  {
                    label: "Bouwjaar",
                    value: dossier.vehicle.buildYear
                      ? String(dossier.vehicle.buildYear)
                      : undefined,
                  },
                  {
                    label: "Km-stand",
                    value:
                      dossier.kilometers !== null &&
                      dossier.kilometers !== undefined
                        ? `${formatNumber(dossier.kilometers)} km`
                        : undefined,
                  },
                ]}
              />
            </div>

            <VehicleImage
              brand={dossier.vehicle.brand}
              model={dossier.vehicle.model}
              color={dossier.vehicle.color}
              src={headerPhoto?.contentUrl}
              srcLabel={headerPhoto ? getMediaIntentLabel(headerPhoto) : null}
              size="lg"
            />
          </div>
        </m.div>

        {(createdAt || sourceLabel || teamName || assigneeName) && (
          <div className="mt-5 flex flex-col gap-2 text-xs text-muted-foreground">
            {(createdAt || sourceLabel) && (
              <p>
                {createdAt && `Aangemaakt op ${createdAt}`}
                {createdAt && sourceLabel && " · "}
                {sourceLabel && `via ${sourceLabel}`}
              </p>
            )}
            {(teamName || assigneeName) && (
              <dl className="flex flex-wrap items-center gap-x-4 gap-y-1">
                {teamName && (
                  <HeaderOwner
                    label="Team"
                    icon={<TeamAvatar assigned className="size-5" />}
                    name={teamName}
                  />
                )}
                {assigneeName && (
                  <HeaderOwner
                    label="Behandelaar"
                    icon={
                      <Avatar
                        name={assigneeName}
                        size="sm"
                        className="size-5 text-[9px]"
                      />
                    }
                    name={assigneeName}
                  />
                )}
              </dl>
            )}
          </div>
        )}
      </Card>
    </m.div>
  );
}

// A "· <text>" meta fragment next to the driver name, e.g. "· Achmea".
function HeaderMeta({ children }: { readonly children: ReactNode }): ReactNode {
  return (
    <span className="flex shrink-0 items-baseline gap-2 text-sm text-muted-foreground">
      <span aria-hidden="true" className="text-muted-foreground/50">
        ·
      </span>
      {children}
    </span>
  );
}

interface HeaderOwnerProps {
  /** Visually hidden: the avatar already tells a team from a person. */
  readonly label: string;
  readonly icon: ReactNode;
  readonly name: string;
}

// Who handles the dossier, under its created date. Only rendered for what is
// actually assigned; an unassigned dossier shows nothing here.
function HeaderOwner({ label, icon, name }: HeaderOwnerProps): ReactNode {
  return (
    <div className="flex items-center gap-1.5">
      <dt className="sr-only">{label}</dt>
      <dd className="flex items-center gap-1.5">
        <span aria-hidden="true" className="inline-flex">
          {icon}
        </span>
        {name}
      </dd>
    </div>
  );
}

interface Field {
  readonly label: string;
  readonly value: string | undefined;
  /** When set, the value renders as a link (e.g. `mailto:`/`tel:`) instead of plain text. */
  readonly href?: string;
}

interface FieldGroupProps {
  readonly title: string;
  readonly icon: LucideIcon;
  readonly fields: readonly Field[];
}

// A labelled group of fields, e.g. "Bestuurder" with naam/e-mail/telefoon.
// A field with an `href` (e-mail, telefoon) renders as a link. The value is
// bolder than the label, mirroring the Ledger concept's FieldGrid.
function FieldGroup({ title, icon: Icon, fields }: FieldGroupProps): ReactNode {
  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <span className="grid size-7 place-items-center rounded-lg bg-muted text-muted-foreground">
          <Icon className="size-4" aria-hidden="true" />
        </span>
        <h2 className="text-sm font-semibold">{title}</h2>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
        {fields.map((field) => (
          <div key={field.label} className="contents">
            <dt className="text-muted-foreground">{field.label}</dt>
            <dd className="truncate font-medium">
              {field.value === undefined ? (
                EMPTY_VALUE
              ) : field.href ? (
                <a
                  href={field.href}
                  className="underline-offset-2 transition-colors hover:text-primary hover:underline"
                >
                  {field.value}
                </a>
              ) : (
                field.value
              )}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function DossierDetailHeaderSkeleton(): ReactNode {
  return (
    <Card className="p-5" role="status" aria-live="polite">
      <span className="sr-only">Dossier wordt geladen…</span>
      <div className="flex items-center gap-3">
        <Skeleton className="h-8 w-28 rounded-[5px]" />
        <Skeleton className="h-6 w-48" />
      </div>
      <Separator className="my-4" />
      <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    </Card>
  );
}
